import { CalendarX, ChevronRight, X } from 'lucide-react'
import { toNumber } from '@/lib/decimal'
import { formatContractHours, formatEuros } from '@/lib/format'
import type { MonthKey, OpportunityAllocation, PlanningCell, PlanningOverview } from '@/lib/planning-types'
import { cn } from '@/lib/utils'
import { separateTarget, UNCERTAIN_FILL } from './CapacityBar'
import { monthLabel } from './month'
import { countsIn } from './stages'

interface PersonMonthPanelProps {
  overview: PlanningOverview
  /** Null = de rij "nog niet toegewezen": wel werk, geen capaciteit. */
  personId: string | null
  displayName: string
  month: MonthKey
  cell: PlanningCell
  /** Staat de omzet uitgesplitst? Een kijkvoorkeur, dus hij hoort bij de pagina. */
  moneyOpen: boolean
  onToggleMoney: () => void
  onClose: () => void
  onOpenOpportunity: (id: string) => void
}

interface Line {
  id: string
  name: string
  context: string
  hours: number
  /** Null zodra er geen bedrag aan te hangen is: vaste prijs, intern of geen tarief. */
  amount: number | null
  committed: boolean
  stageLabel: string | null
}

/**
 * De doorsnede achter een balk: welk werk vult deze uren, hoe zeker is het, wat
 * blijft er over, en wat brengt het op.
 *
 * Dit is meteen de enige plek waar het geld per persoon staat. Dat is met opzet:
 * de kolommen beantwoorden de schermvraag (past dit werk) en een euro naast elke
 * naam in elke maand zou daar tientallen getallen bij zetten voor een vraag die je
 * per persoon stelt en niet per blik.
 *
 * Binnen het paneel geldt dezelfde afweging nog een keer. Het geld staat er als een
 * enkele regel die het bedrag zelf draagt, en pas wie hem openklikt krijgt de
 * uitsplitsing per kans erbij. Zo is de omzet nooit verstopt en nooit in de weg voor
 * wie met uren bezig is. De stand is een voorkeur van de kijker en leeft daarom bij
 * de pagina: hij hoort niet dicht te klappen omdat je een andere persoon aanklikt.
 *
 * Alleen aflezen. Elke kansregel is de doorgang naar de kansenlijst eronder, waar
 * je de uren wel kunt typen; het paneel herhaalt die invoer niet. Dat is dezelfde
 * regel als bij de verdeel-uitsplitsing op het factuurpaneel: in een snelblik
 * staat de uitkomst, en het blok wijst naar de plek waar je kunt handelen.
 */
export function PersonMonthPanel({
  overview,
  personId,
  displayName,
  month,
  cell,
  moneyOpen,
  onToggleMoney,
  onClose,
  onOpenOpportunity,
}: PersonMonthPanelProps) {
  const lines = linesFor(overview, personId, month)
  const peak = lines.reduce((max, line) => Math.max(max, line.hours), 0)
  const unpriced = lines.filter((line) => line.amount === null).length

  const planned = toNumber(cell.plannedHours)
  const uncertain = toNumber(cell.uncertainHours)
  const capacity = cell.capacityHours === null ? null : toNumber(cell.capacityHours)
  const free = cell.freeHours === null ? null : toNumber(cell.freeHours)
  const target = separateTarget(cell)
  const targetPercent = overview.persons.find((person) => person.personId === personId)?.planningTargetPercent ?? null

  // Het bedrag komt uit de cel en niet uit de regels hierboven: dan zegt het
  // paneel per definitie hetzelfde als de kolom waar je op klikte.
  const revenue = toNumber(cell.revenue)
  const months = personId === null ? overview.unassigned.months : (overview.persons.find((person) => person.personId === personId)?.months ?? [])
  const windowRevenue = months.reduce((total, row) => total + toNumber(row.revenue), 0)
  // Een prospect zonder tarief zou anders "Omzet EUR 0,00" melden met een zin
  // erachter waarom dat nul is. Dat is geen antwoord maar ruis: heeft dit werk
  // nergens een bedrag, dan zwijgt het paneel erover.
  const hasMoney = revenue > 0 || windowRevenue > 0 || lines.some((line) => line.amount !== null)

  return (
    <div className="border-t border-hairline bg-fill-1/40 px-5 py-4 animate-fade-in" data-testid="persoon-paneel">
      {/* Breed genoeg voor de uitgesplitste stand, en die breedte staat vast: mee
          laten bewegen zou het hele paneel laten springen bij elke klik. */}
      <div className="max-w-120">
        <header className="flex items-start justify-between gap-3">
          <h4 className="text-sm font-semibold text-ink">
            {displayName} <span className="text-ink-faint">·</span> {monthName(month)}
          </h4>
          <button type="button" aria-label="Sluiten" className="btn btn-ghost -mt-1 h-8 w-8 shrink-0 p-0" onClick={onClose}>
            <X className="h-4 w-4" strokeWidth={1.85} />
          </button>
        </header>

        {lines.length === 0 ? (
          <p className="mt-3 flex items-center gap-2 text-sm text-ink-faint">
            <CalendarX className="h-4 w-4" strokeWidth={1.85} />
            Niets gepland deze maand
          </p>
        ) : (
          <ul className="mt-3 space-y-2.5">
            {lines.map((line) => (
              <li key={line.id}>
                <button
                  type="button"
                  onClick={() => onOpenOpportunity(line.id)}
                  className="block w-full rounded-sm px-1 py-0.5 text-left transition-colors hover:bg-fill-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
                >
                  <span className="flex items-baseline justify-between gap-3">
                    <span className="min-w-0 truncate text-sm text-ink">
                      {line.name}
                      <span className="text-ink-faint"> · {line.context}</span>
                    </span>
                    <span className="flex shrink-0 items-baseline gap-2">
                      {line.stageLabel ? <span className="text-xs text-ink-faint">{line.stageLabel}</span> : null}
                      <span className="num text-sm text-ink-soft">{formatContractHours(line.hours)} u</span>
                      {/* Het bedrag hoort bij de uren ernaast, dus het staat op dezelfde
                          regel en niet in een eigen kolom. Een streepje waar niets te
                          rekenen valt houdt de regels op een lijn en zegt tegelijk dat
                          er geen bedrag is in plaats van nul. */}
                      {moneyOpen ? (
                        <span className="money w-24 shrink-0 text-right text-sm text-ink-faint">
                          {line.amount === null ? '-' : formatEuros(line.amount)}
                        </span>
                      ) : null}
                    </span>
                  </span>
                  {/* Geschaald op de zwaarste regel, niet op de capaciteit: de vraag hier
                      is welke kans het meeste weegt, en op 176 uur worden kleine kansen
                      splinters. */}
                  <span
                    aria-hidden
                    className={cn('mt-1 block h-1.5 rounded-full', line.committed ? 'bg-accent-600' : UNCERTAIN_FILL)}
                    style={{ width: `${Math.max((line.hours / Math.max(peak, 1)) * 100, 4)}%` }}
                  />
                </button>
              </li>
            ))}
          </ul>
        )}

        <dl className="mt-4 space-y-1.5 border-t border-hairline-strong pt-3">
          <Row label="Gepland" value={`${formatContractHours(planned)} u`} caps />
          {uncertain > 0 ? (
            <Row
              label="waarvan verwacht"
              value={`${formatContractHours(uncertain)} u`}
              muted
              marker={<span aria-hidden className={cn('h-2 w-2 rounded-[2px]', UNCERTAIN_FILL)} />}
            />
          ) : null}

          {capacity === null ? (
            <p className="pt-1 text-xs text-ink-faint">
              {personId === null
                ? 'Werk zonder naam: er is niemand aan wie beschikbare uren hangen.'
                : 'Geen beschikbaar-getal: geen contracturen bekend en niets handmatig gezet.'}
            </p>
          ) : (
            <>
              <Row label="Beschikbaar" value={`${formatContractHours(capacity)} u`} hint={basisText(cell)} />
              {/* Een handmatig getal wint van elke afleiding, dus vakantie gaat er niet
                  vanzelf af. Dat hoort te blijken, niet ontdekt te worden. */}
              {cell.capacitySource === 'manual' && (cell.capacityBasis?.vacationDays ?? 0) > 0 ? (
                <p className="text-xs text-status-overdue">
                  {cell.capacityBasis?.vacationDays} dag
                  {cell.capacityBasis?.vacationDays === 1 ? '' : 'en'} vakantie deze maand, niet verrekend
                </p>
              ) : null}
              {/* Het streven staat onder het beschikbaar-getal, want het is er een
                  afgeleide van: hoeveel van die uren zou deze maand idealiter vol
                  zitten. Zonder afspraak is dat de volle beschikbaarheid en zou deze
                  regel het getal erboven herhalen, dus dan blijft hij weg. Boven het
                  streven is geen fout maar een signaal, dus de attentie-kleur en
                  niet de alarmkleur. */}
              {target !== null ? (
                <Row
                  label="Streven"
                  value={`${formatContractHours(target)} u`}
                  hint={targetPercent === null ? null : `${targetPercent}% bezetting`}
                  tone={cell.overTarget && !cell.overbooked ? 'attention' : undefined}
                />
              ) : null}
              {free !== null ? (
                <Row
                  label={free < 0 ? 'Te veel' : 'Vrij'}
                  value={`${formatContractHours(Math.abs(free))} u`}
                  strong
                  tone={free < 0 ? 'overdue' : undefined}
                />
              ) : null}
              <p className="pt-1 text-xs text-ink-faint">beschikbaar min alles wat meetelt</p>
            </>
          )}
        </dl>

        {/*
          De uren en het geld zijn twee assen, dus het bedrag hangt niet onder aan
          de urensom maar staat op zichzelf, achter dezelfde hairline. Dicht draagt
          de regel het bedrag zelf: een uitklapper die dicht niets zegt verstopt
          informatie, deze beantwoordt de vraag al voor je hem opent.

          Eén knop die zijn label houdt en alleen zijn chevron draait. Twee knoppen
          die elkaar vervangen zouden het element onder de focus unmounten.
        */}
        {hasMoney ? (
          <div className="mt-4 border-t border-hairline-strong pt-3">
            <button
              type="button"
              onClick={onToggleMoney}
              aria-expanded={moneyOpen}
              title={moneyOpen ? 'De bedragen per kans verbergen' : 'De bedragen per kans tonen'}
              className="-mx-1 flex w-[calc(100%+0.5rem)] items-baseline justify-between gap-3 rounded-sm px-1 py-0.5 transition-colors hover:bg-fill-2 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-accent-500/40"
            >
              <span className="label-caps flex items-center gap-1">
                <ChevronRight
                  aria-hidden
                  className={cn('h-3.5 w-3.5 text-ink-faint transition-transform', moneyOpen && 'rotate-90')}
                  strokeWidth={1.85}
                />
                Omzet
              </span>
              <span className="money shrink-0 text-sm font-semibold text-ink">{formatEuros(revenue)}</span>
            </button>

            {moneyOpen ? (
              <div className="mt-1 space-y-1">
                {/* Alleen als het iets toevoegt: staat alle omzet van deze persoon
                    toevallig in deze ene maand, dan is dit hetzelfde bedrag nog een
                    keer en leest de tweede regel als een tweede getal. */}
                {overview.months.length > 1 && windowRevenue !== revenue ? (
                  <p className="text-xs text-ink-faint">
                    <span className="money">{formatEuros(windowRevenue)}</span> over de getoonde maanden
                  </p>
                ) : null}
                {unpriced > 0 ? (
                  <p className="text-xs text-ink-faint">
                    {unpriced} {unpriced === 1 ? 'kans' : 'kansen'} zonder bedrag: vaste prijs, eigen werk of geen tarief.
                  </p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  hint,
  caps,
  muted,
  strong,
  tone,
  marker,
}: {
  label: string
  value: string
  hint?: string | null
  caps?: boolean
  muted?: boolean
  strong?: boolean
  tone?: 'overdue' | 'attention'
  marker?: React.ReactNode
}) {
  return (
    <div className="flex items-baseline justify-between gap-3">
      <dt className={cn('flex items-center gap-1.5', caps ? 'label-caps' : 'text-xs', muted && 'text-ink-soft', !caps && !muted && 'text-sm text-ink-soft')}>
        {marker}
        {label}
        {hint ? <span className="text-xs text-ink-faint">· {hint}</span> : null}
      </dt>
      <dd
        className={cn(
          'num shrink-0',
          strong ? 'text-sm font-semibold' : 'text-sm',
          'overdue' === tone
            ? 'text-status-overdue'
            : 'attention' === tone
              ? 'text-status-approval'
              : strong
                ? 'text-ink'
                : 'text-ink-soft',
        )}
      >
        {value}
      </dd>
    </div>
  )
}

/** "22 werkdagen, 1 dag vakantie eraf" of, bij een handmatige waarde, dat het handwerk is. */
function basisText(cell: PlanningCell): string | null {
  if (cell.capacitySource === 'manual') return 'handmatig gezet'
  if (!cell.capacityBasis) return 'uit contracturen'

  const { workdays, vacationDays, holidayDays } = cell.capacityBasis
  const off = [
    vacationDays > 0 ? `${vacationDays} dag${vacationDays === 1 ? '' : 'en'} vakantie` : null,
    holidayDays > 0 ? `${holidayDays} feestdag${holidayDays === 1 ? '' : 'en'}` : null,
  ].filter(Boolean)

  return `${workdays} werkdagen${off.length > 0 ? `, ${off.join(' en ')} eraf` : ''}`
}

/**
 * De kansen die deze cel vullen, van vast naar los: gewonnen eerst, dan aflopend
 * op zekerheid, dan op uren. Zo leest het paneel in dezelfde volgorde als de balk
 * stapelt.
 *
 * Wat je niet meetelt staat hier niet: het paneel is de doorsnede achter die balk,
 * en regels die samen meer tellen dan het getal erboven zetten de kolom en de
 * uitsplitsing tegen elkaar op. Bewerken kan nog steeds, want de kansenlijst
 * eronder blijft wel compleet.
 */
function linesFor(overview: PlanningOverview, personId: string | null, month: MonthKey): Line[] {
  const roster = new Set(overview.persons.map((person) => person.personId))
  // "Nog niet toegewezen" draagt in de kolom ook de uren van wie geen rij meer
  // heeft (inactief geworden nadat er gepland was). Zonder diezelfde regel hier
  // telt de uitsplitsing minder dan de cel erboven.
  const belongs = (allocation: OpportunityAllocation): boolean =>
    personId === null ? allocation.personId === null || !roster.has(allocation.personId) : allocation.personId === personId

  const lines: Line[] = []

  for (const opportunity of overview.opportunities) {
    if (!countsIn(opportunity, overview.stages)) continue

    const rows = opportunity.allocations.filter((allocation) => allocation.month === month && belongs(allocation))
    const hours = rows.reduce((sum, allocation) => sum + toNumber(allocation.hours), 0)
    if (hours <= 0) continue

    const priced = rows.filter((allocation) => allocation.amount !== null)
    const committed = opportunity.status === 'WON'
    lines.push({
      id: opportunity.id,
      name: opportunity.name,
      context: [opportunity.client?.name ?? 'Prospect', opportunity.client?.internal ? 'intern' : null].filter(Boolean).join(' · '),
      hours,
      amount: priced.length === 0 ? null : priced.reduce((sum, allocation) => sum + toNumber(allocation.amount), 0),
      committed,
      stageLabel: committed ? null : opportunity.stageLabel,
    })
  }

  const weight = (line: Line) => (line.committed ? 100 : { Waarschijnlijk: 75, Mogelijk: 50, Speculatief: 25 }[line.stageLabel ?? ''] ?? 0)

  return lines.sort((a, b) => weight(b) - weight(a) || b.hours - a.hours)
}

/** "Oktober", zonder jaartal: dat staat al in de kop van de kaart. */
function monthName(month: MonthKey): string {
  return monthLabel(month).replace(/\s\d{4}$/, '')
}
